import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button, Heading, List } from 'yoso';
import { BaseCard } from '~/components/Card';
import { useChallengeExpect } from '~/components/Challenge/Challenge.hooks';
import { useChallengeRewards } from '~/components/Challenge/Purchase/ChallengePurchase.hooks';
import {
  useChallengeScore,
  useHomeProcedureScoreUpSuccessHandler,
  useProceedScoreUp,
} from '~/components/Challenge/Score/ChallengeScore.hooks';
import { currencySymbol } from '~/components/Currency/Currency.hooks';
import { T } from '~/components/I18n';
import grayedHatCitizen from '~/components/Icon/Citizen/grayedHatCitizen.png';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { Link } from '~/components/Route/Link';
import { format } from '~/components/Util/number';
import styles from './ChallengeScoreStatus.module.scss';

export type ChallengeScoreStatusProps = {
  className?: string;
};

export const ChallengeScoreStatus = ({ className }: ChallengeScoreStatusProps) => {
  const { mutateAsync: proceedScoreUp, isLoading } = useProceedScoreUp({
    onSuccess: useHomeProcedureScoreUpSuccessHandler(),
  });
  const {
    data: { histories, totalPurchaseAmountInUsd, purchaseRewardScore },
    isInitialLoading: isRewardInitialLoading,
  } = useChallengeRewards();
  const {
    data: { score, week, scoreUpScore },
    isInitialLoading: isScoreInitialLoading,
  } = useChallengeScore();

  const {
    data: { myRank },
  } = useChallengeExpect();
  const isInitialLoading = isRewardInitialLoading || isScoreInitialLoading;

  const purchasedItems = useMemo(() => histories.slice(0, 3).reverse(), [histories]);

  const onScoreUpClick = useCallback(() => {
    proceedScoreUp();
  }, [proceedScoreUp]);

  return (
    <div className={classNames(styles.root, { [styles.loading]: isInitialLoading }, className)}>
      <div className={styles.header}>
        <Heading level={4} className={styles.title}>
          <small>
            <T name="citizen_scoresection_title1" />
          </small>
          <T name="citizen_scoresection_title2" options={[week]} />{' '}
          <p className={styles.score}>
            <T
              name="citizen_scoresection_total"
              options={[
                <T
                  name="citizen_unit_score"
                  options={[format(score)]}
                  key={0}
                  className={styles.total}
                />,
              ]}
            />
          </p>
        </Heading>
      </div>
      <List className={styles.body}>
        <li>
          <BaseCard shadow className={styles.card} size="large">
            <div className={styles.cardBody}>
              <Heading level={5} className={styles.cardTitle}>
                <T name="citizen_challengescore_purchasebonus_title" />
              </Heading>
              <p className={styles.cardScore}>
                <T
                  name="citizen_unit_score"
                  options={[
                    <span key={0} className={styles.highlight}>
                      {format(purchaseRewardScore)}
                    </span>,
                  ]}
                />
              </p>
              <p className={styles.cardDescription}>
                {myRank ? (
                  <>
                    <T name="citizen_challengescore_purchaseamount" />{' '}
                    <span>{`${currencySymbol['USD']}${format(totalPurchaseAmountInUsd)}`}</span>
                  </>
                ) : (
                  <T
                    name="citizen_challengescore_purchasebonus_before"
                    options={[format(totalPurchaseAmountInUsd)]}
                  />
                )}
              </p>
            </div>
            <Link
              className={classNames(styles.cardFooter, styles.cardLink)}
              to="challenge/purchase"
              data-event="click"
              data-event-target="score_amount_btn_in_home">
              <div className={styles.cardLinkBody}>
                <List className={styles.imgStack}>
                  {purchasedItems.length > 0 ? (
                    purchasedItems.map((item, index) => (
                      <li key={`${item.name}-${index}`}>
                        <img
                          src={item.thumbnailUrl}
                          className={styles.imgStackItem}
                          alt={item.name}
                          width={50}
                          height={50}
                        />
                      </li>
                    ))
                  ) : (
                    <li>
                      <div className={styles.imgStackEmpty}>
                        <img src={grayedHatCitizen} alt="empty" width={33} height={33} />
                      </div>
                    </li>
                  )}
                </List>
                <T name="citizen_challengescore_purchasebonus_count" />
              </div>
              <p className={styles.cardLinkAside}>
                <T
                  name="citizen_unit_rewardcount"
                  options={{
                    0: format(histories?.length || 0),
                    count: histories?.length || 0,
                  }}
                />{' '}
                <Chevron className={styles.chevron} width={1.5} />
              </p>
            </Link>
          </BaseCard>
        </li>
        <li>
          <BaseCard shadow className={styles.card} size="large">
            <div className={styles.cardBody}>
              <Heading level={5} className={styles.cardTitle}>
                <T name="citizen_challengescore_scoreup_title" />
              </Heading>
              <Link
                to="/challenge/score"
                className={styles.cardScore}
                data-event="click"
                data-event-target="my_challenge_score_up_score">
                <T
                  name="citizen_unit_score"
                  options={[
                    <span key={0} className={styles.highlight}>
                      {format(scoreUpScore)}
                    </span>,
                  ]}
                />
              </Link>
              {!score && (
                <p className={styles.cardDescription}>
                  <T name="citizen_challengescore_scoreup_before" />
                </p>
              )}
            </div>
            <div className={styles.cardFooter}>
              <Button
                disabled={!score || isLoading}
                onClick={onScoreUpClick}
                className={styles.scoreUpButton}
                size="large"
                data-event="click"
                data-event-target="score_up_btn_in_home">
                <T name="citizen_challengescore_scoreup_btn" />
              </Button>
            </div>
          </BaseCard>
        </li>
      </List>
    </div>
  );
};
